import { ParsedUrlQuery } from 'querystring';

import { useEffect, useState, useRef, useMemo } from 'react';

import { FieldErrors, useForm, UseFormHandleSubmit } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'next/navigation';

import { SearchViewModel, ApiError } from '@api/v3';
import { validateFormField } from '@components/views/flight/utils';
import { callService } from '@api/remote-call';
import { SEARCH_LENGTH } from '@utils/utils';
import { useSearchURL } from '@api/data-loaders/resource';

import { paramsToObject } from '../utils';

const requiredValidationMessage = 'Zorunlu Alan';
const useFormValidation = () => {
  const schema = yup.object().shape({
    destination: yup.string().required(requiredValidationMessage),
  });
  return schema;
};

type DestinationSearchFormProps = {
  destinationSearchText?: string;
  destination?: SearchViewModel;
  onDestinationTextChange: (s: string) => void;
  onDestinationChange: (d: SearchViewModel) => void;
  searchItem: SearchViewModel | undefined;
};

type DestinationSearchBarErrorProps = {
  handleSubmit: UseFormHandleSubmit<{ destination: string }>;
  errors: FieldErrors<{ destination: string }>;
  isLoading?: boolean;
  destinationError: boolean;
};

const QueriesConfigMap = {
  tour: [
    'tour-search',
    (s: string) =>
      callService(
        { service: 'SearchesService', method: 'searchesTour', version: 'v3' },
        { s }
      ),
  ] as const,
  hotel: [
    'hotel-search',
    (s: string) =>
      callService(
        { service: 'SearchesService', method: 'searchesHotel', version: 'v3' },
        { s }
      ),
  ] as const,
  ship: [
    'ship-search',
    (s: string) =>
      callService(
        { service: 'SearchesService', method: 'searchesCruise', version: 'v3' },
        { s }
      ),
  ] as const,
};

export const useSearchHook = (
  type: keyof typeof QueriesConfigMap,
  slug: string,
  shouldQuery = true
) => {
  const [queryKey, searchFn] = QueriesConfigMap[type];
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return useQuery<SearchViewModel[]>(
    [queryKey, slug],
    () => searchFn(slug.split('-').join(' ')),
    {
      enabled: slug?.length >= SEARCH_LENGTH && mounted.current && shouldQuery,
      keepPreviousData: !!slug,
    }
  );
};

const useSearchBar = (
  type: 'tour' | 'hotel' | 'ship' = 'hotel',
  searchText = ''
): DestinationSearchFormProps & DestinationSearchBarErrorProps => {
  const [destinationSearchText, setDestinationSearchText] =
    useState<string>(searchText);
  const [destination, setDestination] = useState<SearchViewModel>();
  const [destinationError, setDestinationError] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(
    () => paramsToObject(searchParams?.entries()),
    [searchParams]
  );
  const params = useParams();

  const schema = useFormValidation();
  const urlData = useSearchURL();
  const slug = (params?.slug as string) ?? '';

  const { data: searchItem, error } = useQuery(
    ['filterLabel', slug],
    () =>
      callService(
        { service: 'SearchesService', method: 'searchesUrl', version: 'v3' },
        { s: String(slug) }
      ),

    {
      enabled: Boolean(type === 'tour' || type === 'ship') && slug?.length >= 1,
      initialData: typeof window === 'undefined' ? urlData : undefined,
    }
  );

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      destination: validateFormField(destination) as string,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (searchItem && searchItem?.url === params?.slug) {
      onDestinationChange(searchItem);
    }
  }, [searchItem, query]);

  useEffect(() => {
    if (error) {
      if ((error as ApiError)?.body?.status === 404) {
        setDestinationError(true);
        setDestinationSearchText(searchItem?.text ?? '');
        setDestination(undefined);
      } else {
        setDestinationError(false);
      }
    } else {
      setDestinationError(false);
    }
  }, [error, searchItem]);

  const onDestinationChange = (item: SearchViewModel | undefined) => {
    setValue('destination', validateFormField(item) as string);
    if (item) {
      setDestinationError(false);
      onDestinationTextChange(item.text);
      setDestination(item);
    }
  };

  const onDestinationTextChange = (text = '') => {
    setDestinationSearchText(text);
    if (!text) {
      setDestination(undefined);
      setValue('destination', '');
      setDestinationError(true);
    } else {
      setDestinationError(false);
    }
  };

  return {
    destinationSearchText,
    destination,
    onDestinationTextChange,
    onDestinationChange,
    handleSubmit,
    errors,
    searchItem,
    destinationError,
  };
};

export default useSearchBar;
